import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';

export interface FieldHelpProps {
  /** Do que a explicação trata, em duas ou três palavras. */
  title: string;
  /** A explicação. Português claro, sem jargão: quem lê toca estacionamento. */
  children: ReactNode;
  className?: string;
}

/**
 * A bolinha "?" no canto do rótulo, com a explicação do que aquela parte do
 * sistema faz.
 *
 * Toda explicação mora aqui, e a tela não ensina por texto corrido (#613).
 * Embaixo do campo, no `FieldHint`, fica só o que depende do que foi
 * preenchido: a prévia do preço, um erro, um aviso que impede salvar.
 *
 * Abre no clique, não no hover: o app do operador roda em celular na cabine e o
 * painel também é usado no telefone, onde hover não existe. Por isso usa
 * popover e não o `Tooltip` do pacote, que depende de hover (#403).
 *
 * O brilho entre o branco e o amarelo diz "tem ajuda aqui" sem gritar, e fica
 * parado para quem pediu menos movimento ao sistema.
 */
export const FieldHelp = ({ title, children, className }: FieldHelpProps) => (
  <PopoverPrimitive.Root>
    <PopoverPrimitive.Trigger
      type="button"
      aria-label={`O que é ${title}`}
      className={cn(
        // A área de toque passa da bolinha: 20 px desenhados, 32 px de alvo.
        'relative inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full align-middle',
        "after:absolute after:-inset-1.5 after:content-['']",
        // O "?" vem do CSS para não virar texto do rótulo: "SMS" continua "SMS".
        "before:content-['?']",
        'border border-brand/60 bg-surface text-[11px] font-bold leading-none text-fg',
        'animate-help-glow motion-reduce:animate-none motion-reduce:shadow-[0_0_0_2px_#fff4c2]',
        'transition-colors hover:bg-brand hover:text-brand-fg',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
        className,
      )}
    ></PopoverPrimitive.Trigger>
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        sideOffset={6}
        collisionPadding={12}
        className={cn(
          'z-50 w-[min(20rem,calc(100vw-2rem))] rounded-lg border border-border bg-surface p-3',
          'text-sm shadow-lg focus-visible:outline-none',
        )}
      >
        <p className="font-medium">{title}</p>
        <div className="mt-1 space-y-2 text-muted-fg">{children}</div>
        <PopoverPrimitive.Arrow className="fill-border" />
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  </PopoverPrimitive.Root>
);
